import React, { createContext, useContext, useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { LoadingOverlay } from '@mantine/core'
import { decodeJwt } from 'jose'

// RBAC permissions live in the access token, not the ID token that `user` exposes,
// and auth0-react doesn't decode the access token — so we decode it once here and
// share via context. Not verified client-side (the backend verifies); UI only.
export const PermissionsContext = createContext<string[]>([])

export const usePermissions = (): string[] => useContext(PermissionsContext)

const getPermissions = (accessToken: string): string[] => {
  try {
    const { permissions } = decodeJwt(accessToken)
    return Array.isArray(permissions) ? permissions : []
  } catch {
    return []
  }
}

export const PermissionsProvider: React.FC = ({ children }) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0()
  const [permissions, setPermissions] = useState<string[] | null>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }
    getAccessTokenSilently()
      .then(token => setPermissions(getPermissions(token)))
      .catch(() => setPermissions([]))
  }, [isAuthenticated, getAccessTokenSilently])

  // Resolve before rendering children so the role is correct on first paint
  // (avoids an admin briefly being treated as a reporter).
  if (isAuthenticated && permissions === null) {
    return <LoadingOverlay visible />
  }

  return (
    <PermissionsContext.Provider value={permissions ?? []}>
      {children}
    </PermissionsContext.Provider>
  )
}
